import { Box, Chip, Paper, Stack, TablePagination } from '@mui/material';
import { flexRender } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import {
  AppTable,
  AppTableBody,
  AppTableCell,
  AppTableContainer,
  AppTableHead,
  AppTableRow,
  AppTableSort,
} from '../../../../components/common/app-table/ui';
import useDefaultTable from '../../../../components/common/app-table/useDefaultTable';
import FormAddButton from '../../../../components/common/FormAddButton';
import AppSearchField from '../../../../components/common/forms/AppSearchField';
import { ExportButton, GetTemplateButton } from '../../../../components/common/primebmd-ui/DataButtons';
import { ILocation, ExportLocation  } from './utils/Location-utils';
import LocationColumns from './LocationColumn';
import { ExportLocationToExcel, GetTemplateLocation } from './utils/exporting';
import { useGetLocationsQuery, useExportLocations, useImportLocations } from '../../../../hooks/useLocations';
import LoadingIndicator from '../../../../components/common/LoadingIndicator';
import { useSnackBar } from '../../../../components/ToastProvider';
import MultipleUploadButton from '../../../../components/common/MultipleUploadButton';
import * as xlsx from 'xlsx';
import { usePermission } from "../../../../hooks/usePermissions";
import { Modules, Permissions } from "../../../Users/Roles/roles-utils";
import { Navigate } from 'react-router-dom';

//quins
import { useState, useCallback } from 'react';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useGetUserQuery } from '../../../../hooks/useUser';
import Typography from '@mui/material/Typography';
import { appColors } from '../../../../theme';

const LocationTable = () => {
  const hasPermission = usePermission(Modules.mmdLoc.accessKey);
  const hasAddPermission = usePermission(
    Modules.mmdLoc.accessKey,
    Permissions.add
  );
  const hasEditPermission = usePermission(
    Modules.mmdLoc.accessKey,
    Permissions.edit
  );
  const hasDeletePermission = usePermission(
    Modules.mmdLoc.accessKey,
    Permissions.delete
  );
  const hasImportPermission = usePermission(
    Modules.mmdLoc.accessKey,
    Permissions.import
  );
  const hasExportPermission = usePermission(
    Modules.mmdLoc.accessKey,
    Permissions.export
  );
  const { showSnackBar } = useSnackBar();

  const LocationData = useGetLocationsQuery();
  const importData = useImportLocations();
  const data: ILocation[] = LocationData?.data === undefined ? [] : LocationData.data;
  const formattedData: ExportLocation[] = useExportLocations(data);


  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [selectedLocationForMembers, setSelectedLocationForMembers] = useState<ILocation | null>(null);
  const usersQuery = useGetUserQuery();

  const handleOpenMembers = useCallback((location: ILocation, position: { top: number; left: number }) => {
    setSelectedLocationForMembers(location);
    setAnchorPosition(position);
  }, []);

  const handleCloseMembers = () => {
    setAnchorPosition(null);
    setSelectedLocationForMembers(null);
  };

  const { table, dataCount, searchFilter, setSearchFilter, pageIndex, pageSize } = useDefaultTable<ILocation>(
    data,
    LocationColumns(handleOpenMembers)
  );

 
  
  const navigate = useNavigate();

  if (!hasPermission) return <Navigate to="/home" />;

  const emptyText = searchFilter ? 'No data found' : 'No data yet';

  const gotoAddLocation = () => navigate('/location-new');

  const handleExport = () => {
    ExportLocationToExcel(formattedData, 'Location Database');
  }

  const handleTemplate = () => {
    GetTemplateLocation([], 'Template Location Database');
  
  }

  const readUploadFile = (e : any) => {
    e.preventDefault();
    if(e.target.files[0] !== undefined){
      const checkExcelName = e.target.files[0].name.split("_");
      if (checkExcelName[0].toUpperCase() === 'TEMPLATE LOCATION DATABASE') {
          const reader = new FileReader();
          reader.onload = (e) => {
              const data = e.target?.result;
              const workbook = xlsx.read(data, { type: "array" });
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];
              const json = xlsx.utils.sheet_to_json(worksheet);
              const jsonData: any =  json === undefined ? [] : json;

              const newData = jsonData.map((dataObject: any) => ({ 
                name: dataObject["Name"],
                streetLocation: dataObject["Street Location"],
                type: dataObject["Type"],
              }));

              if(newData.length !== 0){
                importData.mutate(newData);
              }
              else showSnackBar('No data found on excel', 'error');
              setTimeout(() => {
                window.location.reload();
              }, 1000);
          };
          reader.readAsArrayBuffer(e.target.files[0]);
      }
      else showSnackBar('Invalid file upload please download Excel template', 'error');
    }
}

  if (LocationData?.isLoading) return <LoadingIndicator show />;

  return (
    <>
      <Stack direction='row'  justifyContent='flex-end' mb={2} gap={2}>
        {hasImportPermission && (
          <>
            <GetTemplateButton onClick={handleTemplate} />
            <MultipleUploadButton
              accept=".xlsx, .xls"
              onChange={readUploadFile}
            />
          </>
        )}
        {hasExportPermission && <ExportButton onClick={handleExport} />}
        <AppSearchField
          onSearchKeywordChange={setSearchFilter}
          searchKeyword={searchFilter ?? ''}
        />
        {hasAddPermission && <FormAddButton onClick={gotoAddLocation} />}
     
       
      </Stack>
      <Paper elevation={3}>
        <AppTableContainer>
          <AppTable stickyHeader>
            <AppTableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <AppTableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <AppTableCell key={header.id} align='center'>
                      <AppTableSort
                        canSort={header.column.getCanSort()}
                        currentSort={header.column.getIsSorted()}
                        onSort={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </AppTableSort>
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableHead>
            <AppTableBody dataCount={dataCount} loading={LocationData?.isLoading} emptyText={emptyText}>
              {table.getRowModel().rows.map((row) => (
                <AppTableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <AppTableCell key={cell.id} align='center' style={{padding: (!hasEditPermission && !hasDeletePermission ? '12px': undefined)}}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableBody>
          </AppTable>
        </AppTableContainer>
        <Box position='relative'>
          <TablePagination
            sx={{ bgcolor: 'white', borderTop: '1px solid #eee' }}
            component='div'
            count={dataCount}
            page={pageIndex}
            onPageChange={(e, page) => table.setPageIndex(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Box>
      </Paper>
      {LocationData?.isError && showSnackBar('Network Error', 'error')}
            <Popover
        open={Boolean(anchorPosition)}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition || undefined}
        onClose={handleCloseMembers}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        BackdropProps={{
          invisible: true,
          sx: { backgroundColor: 'transparent' },
        }}
        PaperProps={{
          sx: {
            width: 340,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 380,
            p: 2,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            border: '1px solid #e0e0e0',
            bgcolor: '#fff',
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" pb={1} borderBottom="1px solid #eee">
          <Typography fontSize={15} color={appColors.dark} fontWeight={600}>
            {selectedLocationForMembers?.name || ''} - Members
          </Typography>
          <IconButton size="small" onClick={handleCloseMembers} sx={{ p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack gap={1} sx={{ maxHeight: 280, overflowY: 'auto', mt: 1.5, pr: 0.5 }}>
          {(!selectedLocationForMembers?.userIds || selectedLocationForMembers.userIds.length === 0) ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              No members assigned to this location
            </Typography>
          ) : (
            selectedLocationForMembers.userIds.map((uid) => {
              const user = usersQuery.data?.find((u) => u.id === uid);
              const isActive = user ? user.isActive : true;
              return (
                <Box key={uid} sx={{ p: 1.2, borderRadius: 1, bgcolor: '#f9f9f9', border: '1px solid #eee' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography color="text.primary" fontSize={14} fontWeight={500}>
                        {user ? `${user.firstName} ${user.lastName}` : 'Former Member (Deleted User)'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontSize={12}>
                        Role: {user?.roleName || 'N/A'}
                      </Typography>
                    </Box>
                    {user && (
                      <Chip
                        size="small"
                        label={isActive ? 'Active' : 'Inactive'}
                        color={isActive ? 'primary' : 'default'}
                        variant={isActive ? 'outlined' : 'filled'}
                        sx={{
                          flexShrink: 0,
                          height: 20,
                          fontSize: 11,
                          fontWeight: 500,
                          ...(!isActive && {
                            backgroundColor: '#e0e0e0',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                          }),
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              );
            })
          )}
        </Stack>
      </Popover>
    </>
  );
};

export default LocationTable;

